import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { AuthContextType, AuthUser, SignupRequest, UserType } from '../types';
import { normalizeTenDigitPhone } from '../utils/validation';
import AuthService from '../services/authService';
import UserService from '../services/userService';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in and validate token
    const initializeAuth = async () => {
      try {
        const storedUser = AuthService.getCurrentUser();
        const storedToken = AuthService.getToken();

        if (storedUser && storedToken) {
          const isTokenValid = AuthService.validateStoredToken();

          if (isTokenValid) {
            setUser(storedUser);
            setToken(storedToken);
          } else {
            AuthService.logout();
            setUser(null);
            setToken(null);
          }
        }
      } catch (e) {
        console.error('Auth bootstrap failed, clearing session:', e);
        AuthService.logout();
        setUser(null);
        setToken(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (identifier: string, password: string): Promise<AuthUser> => {
    try {
      const id = identifier.trim();
      const payload =
        id.includes('@') ? { identifier: id, password } : { identifier: normalizeTenDigitPhone(id), password };
      const response = await AuthService.login(payload);
      const userData: AuthUser = {
        userId: response.userId,
        phoneNumber: response.phoneNumber,
        email: response.email,
        firstName: response.firstName,
        lastName: response.lastName,
        userType: response.userType,
        role: response.role
      };
      
      setUser(userData);
      setToken(response.token);
      return userData;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const signup = async (signupData: SignupRequest): Promise<void> => {
    try {
      await AuthService.signup(signupData);
      // After successful signup, automatically log in the user
      await login(normalizeTenDigitPhone(signupData.phoneNumber), signupData.password);
    } catch (error) {
      console.error('Signup failed:', error);
      throw error;
    }
  };

  const logout = (): void => {
    AuthService.logout();
    setUser(null);
    setToken(null);
  };

  const refreshUserProfile = useCallback(async () => {
    const current = AuthService.getCurrentUser();
    if (!current) return;
    try {
      const full = await UserService.getUserByPhoneNumber(current.phoneNumber);
      const next: AuthUser = {
        ...current,
        firstName: full.firstName,
        lastName: full.lastName,
        email: full.email,
        userType: full.userType as UserType,
      };
      localStorage.setItem('user', JSON.stringify(next));
      setUser(next);
    } catch (e) {
      console.error('refreshUserProfile failed:', e);
    }
  }, []);

  const value: AuthContextType = {
    user,
    token,
    login,
    signup,
    logout,
    refreshUserProfile,
    isAuthenticated: !!user && !!token,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
